import { displayDateUS } from "../../utils/dateUtils";

/** @param {unknown} value */
function escapeHtml(value) {
  if (value == null || value === "") return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatDate(val) {
  if (!val) return "";
  const s = displayDateUS(val);
  return s || String(val);
}

function valOrDash(v, isDate) {
  if (v == null || v === "") return "—";
  if (isDate) {
    const d = formatDate(v);
    return d ? escapeHtml(d) : "—";
  }
  const s = String(v).trim();
  return s ? escapeHtml(s) : "—";
}

/**
 * Matches EmployeeHealthExamination.jsx fetch logic.
 * @param {{ status?: number, result?: unknown }} response
 * @param {string|number|undefined} targetUserId
 */
export function parseEmployeeHealthExaminationFromApiResponse(
  response,
  targetUserId,
) {
  if (!response || response.status !== 1) return null;
  const { result } = response;
  if (Array.isArray(result)) {
    return result.length > 0 ? result[0] : null;
  }
  if (result?.examination) {
    return result.examination;
  }
  if (result?.items && Array.isArray(result.items)) {
    const uid = parseInt(String(targetUserId), 10);
    return result.items.find((item) => item.userId === uid) || null;
  }
  if (
    result &&
    typeof result === "object" &&
    !Array.isArray(result) &&
    (result.id != null ||
      result.employeeName != null ||
      result.userId != null)
  ) {
    return result;
  }
  return result || null;
}

function signatureBlock(sig) {
  if (sig == null || sig === "") {
    return `<span class="sig-empty">—</span>`;
  }
  const s = String(sig);
  if (s.startsWith("data:")) {
    return `<img class="sig-img" src="${escapeHtml(s)}" alt="" />`;
  }
  return `<span class="sig-text">${escapeHtml(s)}</span>`;
}

/** @param {Record<string, unknown>} record */
function familyTableHtml(record) {
  const rows = Array.isArray(record.familyHistories)
    ? record.familyHistories
    : [];
  if (rows.length === 0) {
    return `<p class="muted">No family history entries.</p>`;
  }
  const body = rows
    .map((r) => {
      if (!r || typeof r !== "object") return "";
      const o = /** @type {Record<string, unknown>} */ (r);
      return `<tr>
        <td class="c-td">${valOrDash(o.familyMember, false)}</td>
        <td class="c-td">${valOrDash(o.illness, false)}</td>
        <td class="c-td">${valOrDash(o.relationship, false)}</td>
      </tr>`;
    })
    .join("");
  return `<table class="c-table">
    <thead><tr>
      <th class="c-th">Family member</th>
      <th class="c-th">Illness</th>
      <th class="c-th">Relationship</th>
    </tr></thead>
    <tbody>${body}</tbody>
  </table>`;
}

/**
 * @param {Record<string, unknown>} record
 * @param {[string, string, boolean?][]} pairs
 */
function fieldGridHtml(record, pairs) {
  const cells = pairs
    .map(([key, label, isDate]) => {
      const v = valOrDash(record[key], !!isDate);
      return `<div class="cell"><div class="fl">${escapeHtml(label)}</div><div class="fv">${v}</div></div>`;
    })
    .join("");
  return `<div class="grid4">${cells}</div>`;
}

/**
 * @param {Record<string, unknown>} record
 * @param {{ companyName: string; logoUrl: string }} branding
 */
export function buildEmployeeHealthExaminationPrintHtml(record, branding) {
  const company = escapeHtml(branding.companyName || "Company");
  const logoUrl = escapeHtml(branding.logoUrl || "");

  const employeeName = escapeHtml(
    String(record.employeeName || record.applicantName || "").trim(),
  );
  const position = escapeHtml(String(record.position || ""));
  const positionApplied = escapeHtml(
    String(record.positionAppliedFor || ""),
  );
  const patientTitle = escapeHtml(String(record.patientTitle || ""));
  const patientName = escapeHtml(String(record.patientName || ""));

  const vitalsPairs = /** @type {[string, string, boolean?][]} */ ([
    ["height", "Height", false],
    ["weight", "Weight", false],
    ["bloodPressure", "Blood pressure", false],
    ["pulse", "Pulse", false],
  ]);

  const testsPairs = /** @type {[string, string, boolean?][]} */ ([
    ["ppdTestResult", "PPD test result", false],
    ["mantoux", "Mantoux (mm)", false],
    ["ppdDateAdministered", "PPD date administered", true],
    ["chestXrayResult", "Chest X-ray result", false],
    ["chestXrayDateAdministered", "Chest X-ray date administered", true],
  ]);

  const findingsPairs = /** @type {[string, string, boolean?][]} */ ([
    ["eyes", "Eyes", false],
    ["hearing", "Hearing", false],
    ["nose", "Nose", false],
    ["throat", "Throat", false],
    ["teeth", "Teeth", false],
    ["tongue", "Tongue", false],
    ["adenopathy", "Adenopathy", false],
    ["chestBreathSounds", "Chest / breath sounds", false],
    ["heartSize", "Heart size", false],
    ["murmur", "Murmur", false],
    ["rhythm", "Rhythm", false],
    ["arteries", "Arteries", false],
    ["abdomen", "Abdomen", false],
    ["rectal", "Rectal", false],
    ["reflexes", "Reflexes", false],
    ["resonance", "Resonance", false],
  ]);

  const es = record.employeeSignature;
  const nestedEmpSig =
    es && typeof es === "object"
      ? /** @type {{ signature?: string }} */ (es).signature
      : null;
  const applicantSig =
    record.applicantSignature ||
    record.empSignature ||
    nestedEmpSig;
  const applicantSignedAt = escapeHtml(
    formatDate(
      record.applicantSignedAt ||
        record.applicantSignatureDate ||
        record.empSignedAt,
    ),
  );
  const applicantSignedBy = escapeHtml(
    String(record.applicantSignedBy || "").trim(),
  );

  const reviewedBySignedBy = escapeHtml(
    String(record.reviewedBySignedBy || "").trim(),
  );
  const reviewedBySig =
    record.reviewedBySignature || record.adminSignature;
  const reviewedBySignedAt = escapeHtml(
    formatDate(
      record.reviewedBySignedAt ||
        record.adminSignatureDate ||
        record.reviewedBySignatureDate,
    ),
  );

  const displayName = employeeName || applicantSignedBy || "—";

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Employee health examination — ${employeeName || "Employee"}</title>
  <style>
    @page { margin: 0.35in; size: letter; }
    * { box-sizing: border-box; }
    body {
      font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
      font-size: 9.5px;
      line-height: 1.4;
      color: #111827;
      margin: 0;
      padding: 10px;
    }
    .sheet { max-width: 920px; margin: 0 auto; }
    @media screen {
      html {
        background: linear-gradient(168deg, #cbd5e1 0%, #e2e8f0 42%, #f1f5f9 100%);
        min-height: 100%;
      }
      body { padding: 24px 14px 34px; background: transparent; min-height: 100vh; }
      .sheet {
        background: #fff;
        padding: 26px 28px 32px;
        border: 1px solid #e2e8f0;
        box-shadow: 0 1px 2px rgba(15,23,42,0.05), 0 24px 48px -16px rgba(15,23,42,0.2);
      }
    }
    @media print {
      html { background: #fff !important; }
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        padding: 0 !important;
        margin: 0;
        background: #fff !important;
      }
      .sheet { padding: 0 !important; border: none !important; box-shadow: none !important; }
    }
    .top {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      padding-bottom: 10px;
      border-bottom: 1px solid #e5e7eb;
      margin-bottom: 12px;
    }
    .logo { height: 48px; width: auto; max-width: 180px; object-fit: contain; }
    .title-block { text-align: right; flex: 1; }
    .title-main { font-size: 14px; font-weight: 700; margin: 0 0 3px 0; }
    .title-sub { font-size: 11px; font-weight: 500; color: #2563eb; margin: 0; }
    .sec { margin-bottom: 11px; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
    .sec-h {
      background: #f8fafc;
      padding: 7px 10px;
      font-size: 9px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      border-bottom: 1px solid #e2e8f0;
    }
    .sec-h.blue { background: #eff6ff; color: #1e3a8a; border-color: #bfdbfe; }
    .pad { padding: 10px; }
    .fl { font-size: 7.5px; font-weight: 600; color: #6b7280; text-transform: uppercase; margin-bottom: 3px; }
    .fv {
      border: 1px solid #e5e7eb;
      border-radius: 5px;
      padding: 5px 7px;
      min-height: 26px;
      font-size: 9.5px;
    }
    .fv.multiline { min-height: 48px; white-space: pre-wrap; }
    .grid3 {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 8px 10px;
    }
    .grid4 {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr 1fr;
      gap: 6px 8px;
    }
    .cell { min-width: 0; }
    .c-table { width: 100%; border-collapse: collapse; font-size: 9px; }
    .c-th {
      text-align: left;
      padding: 5px 8px;
      background: #f9fafb;
      border-bottom: 1px solid #e5e7eb;
      font-weight: 700;
    }
    .c-td { padding: 5px 8px; border-top: 1px solid #f1f5f9; }
    .muted { color: #94a3b8; font-size: 9px; margin: 6px 0; }
    .two-col {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 11px;
      margin-top: 12px;
    }
    .sig-card {
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 9px;
      background: #fafafa;
    }
    .sig-card h3 { font-size: 10px; font-weight: 700; margin: 0 0 7px 0; }
    .sig-label { font-size: 7.5px; font-weight: 600; color: #6b7280; margin: 5px 0 2px 0; }
    .sig-line {
      border-bottom: 1px solid #9ca3af;
      min-height: 24px;
      font-size: 9.5px;
      padding: 2px 0;
    }
    .sig-img { max-height: 38px; max-width: 100%; object-fit: contain; }
    .sig-empty { color: #9ca3af; }
    .footer-print {
      margin-top: 14px;
      padding-top: 7px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
      font-size: 7.5px;
      color: #6b7280;
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="top">
      <img class="logo" src="${logoUrl}" alt="" onerror="this.style.display='none'" />
      <div class="title-block">
        <p class="title-main">Employee health examination</p>
        <p class="title-sub">${company}</p>
      </div>
    </div>

    <div class="sec">
      <div class="sec-h">Employee information</div>
      <div class="pad grid3">
        <div class="cell"><div class="fl">Employee name</div><div class="fv">${employeeName || "—"}</div></div>
        <div class="cell"><div class="fl">Position</div><div class="fv">${position || "—"}</div></div>
        <div class="cell"><div class="fl">Position applied for</div><div class="fv">${positionApplied || "—"}</div></div>
        <div class="cell"><div class="fl">Title (Mr/Mrs/Ms)</div><div class="fv">${patientTitle || "—"}</div></div>
        <div class="cell" style="grid-column: span 2;"><div class="fl">Patient name (employee)</div><div class="fv">${patientName || "—"}</div></div>
      </div>
    </div>

    <div class="sec">
      <div class="sec-h">Family history</div>
      <div class="pad">${familyTableHtml(record)}</div>
    </div>

    <div class="sec">
      <div class="sec-h blue">Physical examination — vitals</div>
      <div class="pad">${fieldGridHtml(record, vitalsPairs)}</div>
    </div>

    <div class="sec">
      <div class="sec-h blue">Tests</div>
      <div class="pad">${fieldGridHtml(record, testsPairs)}</div>
    </div>

    <div class="sec">
      <div class="sec-h blue">Physical findings (normal / abnormal)</div>
      <div class="pad">${fieldGridHtml(record, findingsPairs)}</div>
    </div>

    <div class="sec">
      <div class="sec-h">Physician certification</div>
      <div class="pad">
        <div class="fl">Is patient free from communicable disease?</div>
        <div class="fv multiline" style="margin-bottom:8px;">${valOrDash(record.conditionException, false)}</div>
        <div class="fl">Statement of qualification</div>
        <div class="fv multiline" style="margin-bottom:10px;">${valOrDash(record.noHazardStatement, false)}</div>
        <div class="grid3">
          <div><div class="fl">Physician name</div><div class="fv">${valOrDash(record.physicianName, false)}</div></div>
          <div><div class="fl">Physician phone</div><div class="fv">${valOrDash(record.physicianPhone, false)}</div></div>
          <div><div class="fl">Physician date</div><div class="fv">${valOrDash(record.physicianDate, true)}</div></div>
        </div>
        <div style="margin-top:8px;"><div class="fl">Physician address</div><div class="fv multiline">${valOrDash(record.physicianAddress, false)}</div></div>
        <div style="margin-top:8px;"><div class="fl">Physician signature</div><div class="sig-line">${signatureBlock(record.physicianSignature)}</div></div>
      </div>
    </div>

    <div class="two-col">
      <div class="sig-card">
        <h3>Employee</h3>
        <div class="sig-label">Name</div>
        <div class="fv" style="margin-bottom:5px;">${displayName}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(applicantSig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${applicantSignedAt || "—"}</div>
      </div>
      <div class="sig-card">
        <h3>Admin / reviewer</h3>
        <div class="sig-label">Name</div>
        <div class="fv" style="margin-bottom:5px;">${reviewedBySignedBy || "—"}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(reviewedBySig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${reviewedBySignedAt || "—"}</div>
      </div>
    </div>

    <div class="footer-print">${company}</div>
  </div>
</body>
</html>`;
}
